import { autocompleteClasses, Box, Link, Stack, styled, Theme, Tooltip } from '@chronosphereio/chrono-ui';
import { isTimeZoneOverridden as isTimeZoneOverriddenFn } from '../utils/isTimeZoneOverridden';
import { UseTimeZoneDetailsResult } from '../utils/useTimeZoneDetails';

export interface TimeRangeSelectTimeZoneProps {
  userTimeZone: string;
  timeZoneDetails: UseTimeZoneDetailsResult;
  onTimeZoneClick?: (timeZone: string) => void;
  disableTimeZoneOverride?: boolean;
}

export const TimeRangeSelectTimeZone = ({
  userTimeZone: userTimeZoneIANA,
  timeZoneDetails: { shortUTCOffset, longUTCOffset, IANA, shortName, timezonePreferencesEnabled },
  onTimeZoneClick,
  disableTimeZoneOverride,
}: TimeRangeSelectTimeZoneProps) => {
  const isTimeZoneOverridden = isTimeZoneOverriddenFn(userTimeZoneIANA, IANA);

  // In theory, there are some edge cases where the short name might be undefined. In some rare
  // cases (e.g. UTC), the IANA and short name are the same, so it makes sense to dedupe them.
  const uniqueDedupedNames = [...new Set([IANA, shortName].filter((name) => !!name))];
  const namesCombined = uniqueDedupedNames.join(', ');

  const tooltipContent = (
    <Stack spacing={2}>
      <div>
        <TooltipTimeZoneText isOverriden={isTimeZoneOverridden} sx={{ whiteSpace: 'nowrap' }}>
          {namesCombined}
        </TooltipTimeZoneText>
        <TooltipTimeZoneText isOverriden={isTimeZoneOverridden}>{longUTCOffset}</TooltipTimeZoneText>
        {isTimeZoneOverridden && (
          <TooltipTimeZoneText isOverriden={isTimeZoneOverridden}>
            {disableTimeZoneOverride ? `(Required page time zone)` : `(Page override)`}
          </TooltipTimeZoneText>
        )}
        {timezonePreferencesEnabled && !isTimeZoneOverridden && <div>(Account time zone)</div>}
      </div>
      {timezonePreferencesEnabled && (
        <TooltipDescription>
          To change your account time zone, click your profile icon in the upper right corner.
        </TooltipDescription>
      )}

      {disableTimeZoneOverride ? (
        <TooltipDescription>
          The time zone for this page is set to {shortName || shortUTCOffset} and cannot be overridden.
        </TooltipDescription>
      ) : (
        <TooltipDescription>
          To temporarily override the time zone on this page, click to select a new time zone.
        </TooltipDescription>
      )}
    </Stack>
  );

  const getLinkColor = (theme: Theme) => {
    if (isTimeZoneOverridden) {
      if (theme.palette.mode === 'light') {
        return theme.palette.status.warning.highContrast;
      }
      return theme.palette.status.warning.main;
    }
    return 'inherit';
  };

  const timeZoneLink = (
    <span>
      <Link
        component="button"
        type="button"
        disabled={disableTimeZoneOverride}
        onClick={() => {
          if (!disableTimeZoneOverride && onTimeZoneClick) {
            onTimeZoneClick(IANA);
          }
        }}
        color={getLinkColor}
        variant="body2"
        lineHeight="inherit"
        sx={{ cursor: disableTimeZoneOverride ? 'default' : 'auto' }}
      >
        {shortName || shortUTCOffset}
      </Link>
    </span>
  );

  return <Tooltip Content={tooltipContent}>{timeZoneLink}</Tooltip>;
};

const TooltipTimeZoneText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOverriden',
})<{
  isOverriden: boolean;
}>(({ theme, isOverriden }) => {
  const overrideColor = theme.featureFlags.panwRebrandStubYear
    ? theme.palette.text.warning
    : theme.palette.status.warning.main;

  return {
    color: isOverriden ? overrideColor : 'inherit',
  };
});

const TooltipDescription = styled(Box)({
  // Make this text width fit somewhere between 180px (from designs) and the width of
  // the parent, which will occasionally be larger than 180px because of very long
  // IANA time zone names.
  width: 'min-content',
  minWidth: '180px',
  maxWidth: '100%',
});

export const TimeZoneAutocompleteWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2, 0, 2),
  backgroundColor: theme.palette.background.default,
  // These styles are needed to ensure the box height accounts for the height of the autocomplete dropdown
  [`& .${autocompleteClasses.popper}`]: {
    position: 'relative !important',
    transform: 'none !important',
  },
}));
